import React from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { Button, Input, Select, Typography } from 'antd'

import { BuyersDetails } from '../types'
import { FlexRow } from '../../../FlexboxReusables/FlexItems'
import { Currencies } from '../../../../globals'

type BuyersDetailsFormProps = {
  buyersDetails: BuyersDetails
  setBuyersDetails: React.Dispatch<React.SetStateAction<BuyersDetails>>
  invoiceCurrency: Currencies
  setInvoiceCurrency: React.Dispatch<React.SetStateAction<Currencies>>
}

export const BuyersDetailsForm: React.FC<BuyersDetailsFormProps> = ({
  buyersDetails,
  setBuyersDetails,
  invoiceCurrency,
  setInvoiceCurrency
}) => {
  const isDevEnvironment = process.env.NODE_ENV === 'development'

  const mockCompanyData = () => {
    setBuyersDetails({
      companyName: 'Test Company',
      address: 'Test Address',
      zipCode: '12345',
      city: 'Test City',
      country: 'Test Country',
      email: 'somedude@largecorp.com',
      companyEmail: 'somedude@largecorp.com',
      salutationOfContactPerson: 'Herr',
      contactPerson: 'Some Dude',
      phoneNumber: '1234567890'
    })
  }

  return (
    <>
      <FlexRow>
        <InputContainer>
          <InputLabel>
            <FormattedMessage id='generic.companyName' />
            <RequiredIndicator>*</RequiredIndicator>
          </InputLabel>
          <StyledInput
            value={buyersDetails.companyName}
            onChange={(e) => setBuyersDetails({ ...buyersDetails, companyName: e.target.value })}
            required
          />
        </InputContainer>
      </FlexRow>

      <FlexRow>
        <InputContainer>
          <InputLabel>
            <FormattedMessage id='generic.companyAddress' />
            <RequiredIndicator>*</RequiredIndicator>
          </InputLabel>
          <StyledInput
            value={buyersDetails.address}
            onChange={(e) => setBuyersDetails({ ...buyersDetails, address: e.target.value })}
            required
          />
        </InputContainer>
      </FlexRow>

      <FlexRow>
        <InputContainer>
          <InputLabel>
            <FormattedMessage id='generic.zipCode' />
            <RequiredIndicator>*</RequiredIndicator>
          </InputLabel>
          <StyledInput
            value={buyersDetails.zipCode}
            onChange={(e) => setBuyersDetails({ ...buyersDetails, zipCode: e.target.value })}
            required
          />
        </InputContainer>
      </FlexRow>

      <FlexRow>
        <InputContainer>
          <InputLabel>
            <FormattedMessage id='generic.city' />
            <RequiredIndicator>*</RequiredIndicator>
          </InputLabel>
          <StyledInput
            value={buyersDetails.city}
            onChange={(e) => setBuyersDetails({ ...buyersDetails, city: e.target.value })}
            required
          />
        </InputContainer>
      </FlexRow>

      <FlexRow>
        <InputContainer>
          <InputLabel>
            <FormattedMessage id='generic.country' />
            <RequiredIndicator>*</RequiredIndicator>
          </InputLabel>
          <StyledInput
            value={buyersDetails.country}
            onChange={(e) => setBuyersDetails({ ...buyersDetails, country: e.target.value })}
            required
          />
        </InputContainer>
      </FlexRow>

      <FlexRow>
        <InputContainer>
          <InputLabel>
            Email
            <RequiredIndicator>*</RequiredIndicator>
          </InputLabel>
          <StyledInput
            value={buyersDetails.email}
            onChange={(e) => setBuyersDetails({ ...buyersDetails, email: e.target.value })}
            required
          />
        </InputContainer>
      </FlexRow>

      <FlexRow>
        <InputContainer>
          <InputLabel>
            <FormattedMessage id='generic.companyEmail' />
            <RequiredIndicator>*</RequiredIndicator>
          </InputLabel>
          <StyledInput
            value={buyersDetails.companyEmail}
            onChange={(e) => setBuyersDetails({ ...buyersDetails, companyEmail: e.target.value })}
            required
          />
        </InputContainer>
      </FlexRow>

      <FlexRow>
        <InputContainer>
          <InputLabel>
            <FormattedMessage id='generic.contactPerson' />
            <RequiredIndicator>*</RequiredIndicator>
          </InputLabel>
          <StyledSelect
            defaultValue='Herr'
            value={buyersDetails.salutationOfContactPerson}
            options={[
              { value: 'Herr', label: 'Herr' },
              { value: 'Frau', label: 'Frau' }
            ]}
            style={{ width: '10em', }}
            onChange={(value) => setBuyersDetails({ ...buyersDetails, salutationOfContactPerson: value as 'Herr' | 'Frau' })}
          />
          <StyledInput
            value={buyersDetails.contactPerson}
            onChange={(e) => setBuyersDetails({ ...buyersDetails, contactPerson: e.target.value })}
            required
          />
        </InputContainer>
      </FlexRow>

      <FlexRow>
        <InputContainer>
          <InputLabel>
            <FormattedMessage id='generic.phone' />
          </InputLabel>
          <StyledInput
            value={buyersDetails.phoneNumber}
            onChange={(e) => setBuyersDetails({ ...buyersDetails, phoneNumber: e.target.value })}
          />
        </InputContainer>
      </FlexRow>

      <FlexRow>
        <InputContainer>
          <InputLabel>
            Präferierte Währung für die Rechnung
          </InputLabel>
          <Select
            defaultValue={invoiceCurrency}
            options={[
              { value: Currencies.EUR, label: 'EUR' },
              { value: Currencies.CHF, label: 'CHF' }
            ]}
            onChange={(value) => setInvoiceCurrency(value)} />
        </InputContainer>
      </FlexRow>

      {isDevEnvironment && (
        <FlexRow justifyContent='flex-start' width='50%' margin='2em auto'>
          <Button
            type='primary'
            onClick={mockCompanyData}
            style={{ backgroundColor: 'red' }}
          >
            Testdaten einfügen
          </Button>
          Der Button ist nur in der Entwicklungsumgebung sichtbar.
        </FlexRow>
      )}
    </>
  )
}

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  width: 50%;
  max-width: 1000px;
  margin: 0.5em 5em;
  font-size: 1rem;
`

const InputLabel = styled(Typography.Text)`
  flex-basis: 20%;
  font-size: 1rem;
`

const StyledInput = styled(Input)`
  flex-basis: 80%;
`

const StyledSelect = styled(Select)`
  flex-basis: 10%;
  margin-right: 5px;
  margin-left: 25px;
`

const RequiredIndicator = styled.span`
  color: red;
  margin-left: 0.2em;
`
